import { useState, useEffect } from 'react';
import { MessageSquare, MapPin, Activity, Bot, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { IntegrationStatusCard } from './IntegrationStatusCard';

interface IntegrationStatus {
  gohighlevel: {
    enabled: boolean;
    connected: boolean;
  };
  samsara: {
    enabled: boolean;
    connected: boolean;
  };
  eld: {
    enabled: boolean;
    connected: boolean;
  };
  chatgpt: {
    enabled: boolean;
    connected: boolean;
  };
  pdfco: {
    enabled: boolean;
    connected: boolean;
  };
  resend: {
    enabled: boolean;
    connected: boolean;
  };
}

export function IntegrationsOverview() {
  const [status, setStatus] = useState<IntegrationStatus>({
    gohighlevel: { enabled: false, connected: false },
    samsara: { enabled: false, connected: false },
    eld: { enabled: false, connected: false },
    chatgpt: { enabled: false, connected: false },
    pdfco: { enabled: false, connected: false },
    resend: { enabled: false, connected: false },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIntegrationStatus();
  }, []);

  const loadIntegrationStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', [
          'gohighlevel_api_key',
          'gohighlevel_enabled',
          'samsara_api_key',
          'samsara_enabled',
          'chatgpt_api_key',
          'chatgpt_enabled',
          'pdfco_api_key',
          'pdfco_enabled',
          'eld_api_key',
          'eld_enabled',
          'resend_api_key',
          'resend_enabled',
        ]);

      if (error) throw error;

      const newStatus = { ...status };

      if (data) {
        // GoHighLevel
        const ghlApiKey = data.find(s => s.setting_key === 'gohighlevel_api_key')?.setting_value;
        const ghlEnabled = data.find(s => s.setting_key === 'gohighlevel_enabled')?.setting_value === 'true';
        newStatus.gohighlevel = {
          enabled: ghlEnabled,
          connected: !!ghlApiKey && ghlApiKey.length > 0,
        };

        // Samsara
        const samsaraApiKey = data.find(s => s.setting_key === 'samsara_api_key')?.setting_value;
        const samsaraEnabled = data.find(s => s.setting_key === 'samsara_enabled')?.setting_value === 'true';
        newStatus.samsara = {
          enabled: samsaraEnabled,
          connected: !!samsaraApiKey && samsaraApiKey.length > 0,
        };

        // ChatGPT
        const chatgptApiKey = data.find(s => s.setting_key === 'chatgpt_api_key')?.setting_value;
        const chatgptEnabled = data.find(s => s.setting_key === 'chatgpt_enabled')?.setting_value === 'true';
        newStatus.chatgpt = {
          enabled: chatgptEnabled,
          connected: !!chatgptApiKey && chatgptApiKey.length > 0,
        };

        // PDF.co
        const pdfcoApiKey = data.find(s => s.setting_key === 'pdfco_api_key')?.setting_value;
        const pdfcoEnabled = data.find(s => s.setting_key === 'pdfco_enabled')?.setting_value === 'true';
        newStatus.pdfco = {
          enabled: pdfcoEnabled,
          connected: !!pdfcoApiKey && pdfcoApiKey.length > 0,
        };

        // ELD
        const eldApiKey = data.find(s => s.setting_key === 'eld_api_key')?.setting_value;
        const eldEnabled = data.find(s => s.setting_key === 'eld_enabled')?.setting_value === 'true';
        newStatus.eld = {
          enabled: eldEnabled,
          connected: !!eldApiKey && eldApiKey.length > 0,
        };

        // Resend Email
        const resendApiKey = data.find(s => s.setting_key === 'resend_api_key')?.setting_value;
        const resendEnabled = data.find(s => s.setting_key === 'resend_enabled')?.setting_value === 'true';
        newStatus.resend = {
          enabled: resendEnabled,
          connected: !!resendApiKey && resendApiKey.length > 0,
        };
      }

      setStatus(newStatus);
    } catch (error) {
      console.error('Error loading integration status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-1/4"></div>
          <div className="h-20 bg-slate-200 rounded"></div>
          <div className="h-20 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">Integration Status</h3>
        <p className="text-sm text-slate-600">
          Overview of all connected third-party services
        </p>
      </div>

      <div className="space-y-3">
        <IntegrationStatusCard
          name="GoHighLevel SMS"
          description="SMS notifications for drivers"
          isConnected={status.gohighlevel.connected}
          isEnabled={status.gohighlevel.enabled}
          icon={<MessageSquare className="w-5 h-5 text-green-600" />}
          colorClass="bg-green-100"
        />

        <IntegrationStatusCard
          name="Samsara GPS"
          description="Real-time GPS tracking and fleet management"
          isConnected={status.samsara.connected}
          isEnabled={status.samsara.enabled}
          icon={<MapPin className="w-5 h-5 text-blue-600" />}
          colorClass="bg-blue-100"
        />

        <IntegrationStatusCard
          name="ELD Integration"
          description="Hours of Service (HOS) compliance"
          isConnected={status.eld.connected}
          isEnabled={status.eld.enabled}
          icon={<Activity className="w-5 h-5 text-green-600" />}
          colorClass="bg-green-100"
        />

        <IntegrationStatusCard
          name="ChatGPT AI Assistant"
          description="Intelligent AI assistant for fleet data queries"
          isConnected={status.chatgpt.connected}
          isEnabled={status.chatgpt.enabled}
          icon={<Bot className="w-5 h-5 text-purple-600" />}
          colorClass="bg-purple-100"
        />

        <IntegrationStatusCard
          name="Resend Email"
          description="Transactional email delivery"
          isConnected={status.resend.connected}
          isEnabled={status.resend.enabled}
          icon={<Mail className="w-5 h-5 text-indigo-600" />}
          colorClass="bg-indigo-100"
        />
      </div>

      <div className="mt-6 pt-6 border-t border-slate-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">
            {Object.values(status).filter(s => s.connected).length} of 5 integrations active
          </span>
          <button
            onClick={loadIntegrationStatus}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Refresh Status
          </button>
        </div>
      </div>
    </div>
  );
}
